import { CategoryDocument } from '@models/category';
import { ShopByVehicle } from '@ui/ShopByVehicle';
import ShopYourGarage from '@ui/ShopYourGarage';
import { useCategoryFilters } from '@util/hooks/useCategoryFilters';
import useProductsPage, {
  PageSideFilters,
  TopFilters,
} from '@util/hooks/useProductsPage';
import { Pagination } from '@util/types/paginator';
import { slugify } from '@util/urlHelpers';
import SearchControls from 'app/search/components/SearchControls';
import { useAuth } from 'context/AuthContext';
import { FacetCounts, ProductDocument } from 'models/product';
import { Bike, Make, RideType, SellerType, UserBike } from 'models/shared';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useState } from 'react';
import Filters from './filters/Filters';
import CategoryPageHeader from './headers/CategoryPageHeader';
import MasterProductCategoryHeader from './headers/MasterProductCategoryHeader';
import { PageMainContent } from './products/PageMainContent';
import { useBikeFilter } from 'context/BikeFilterContext';
export type CategoryPageSideFilters = Omit<PageSideFilters, 'category'>;

export type CategoryOrSubcategoryPageProps = {
  current_cat: CategoryDocument | null;
  categories: CategoryDocument[];
  color?: string[];
  category: string | null;
  category1: string | null;
  category2: string | null;
  category3: string | null;
  results: ProductDocument[];
  pageInfo: Pagination;
  initialTopFilters: TopFilters;
  initialSideFilters: CategoryPageSideFilters;
  keyword: string | null;
  is_featured?: boolean;
  is_auction?: boolean;
  on_sale: boolean;
  curated_list?: string | null;
  seller_id?: string | null;
  allBrands?: string[];
  brands: string[];
  facet_counts?: FacetCounts;
  useClient?: boolean;
  bootSizes?: string[];
  sellerType?: SellerType[];
  shouldUseRecommend?: boolean;
  h1?: string;
  bottomText?: string;
  rideTypes?: RideType[];
  showSearch?: boolean;
  pageType?: string;
  mostly_parts?: boolean;
};

export default function CategoryOrSubcategoryPage(
  props: CategoryOrSubcategoryPageProps
) {
  const {
    current_cat,
    keyword,
    categories,
    category,
    category1,
    category2,
    category3,
    results,
    on_sale,
    pageInfo: initialPageInfo,
    initialTopFilters,
    initialSideFilters,
    brands,
    allBrands,
    facet_counts,
    color,
    mostly_parts,
  } = props;

  const {
    buttonToggles,
    sideFilters,
    topFilters,
    handleSideFiltersChange,
    handleSortChange,
    setDrawerOpen,
    isFetching,
    setIsFetching,
    handleSearchChange,
    clearAllFilters,
  } = useProductsPage({
    current_cat,
    results,
    pageInfo: initialPageInfo,
    initialTopFilters,
    initialSideFilters,
    categories,
    category,
    category1,
    category2,
    category3,
    keyword,
    on_sale,
    brands,
    facet_counts,
    color,
  });

  const { userDoc } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { setSelectedBike, clearSelectedBike } = useBikeFilter();
  const [showMobileEditGarageModal, setShowMobileEditGarageModal] =
    useState(false);

  const onBikeComplete = useCallback(
    (bike: Bike | null) => {
      if (bike === null) {
        handleSideFiltersChange({
          ...sideFilters,
          bikeYears: [],
          bikeMakes: [],
          bikeModels: [],
        });
        clearSelectedBike();
        return;
      }
      if (!bike.year || !bike.make || !bike.model) return;
      handleSideFiltersChange({
        ...sideFilters,
        bikeYears: [`${bike.year}`],
        bikeMakes: [bike.make],
        bikeModels: [bike.model ?? ''],
      });
      setSelectedBike(bike);
    },
    [handleSideFiltersChange, sideFilters, setSelectedBike, clearSelectedBike]
  );

  const pathname = usePathname() ?? '';

  const handleFilterRemove = useCallback(
    (filter: { key: string; value: any }) => {
      const { key, value } = filter;
      const newFilters = { ...sideFilters };
      if (Array.isArray(newFilters[key as keyof typeof newFilters])) {
        (newFilters[key as keyof typeof newFilters] as any[]) = (
          newFilters[key as keyof typeof newFilters] as any[]
        ).filter((v) => v !== value);
      } else if (
        typeof newFilters[key as keyof typeof newFilters] === 'object' &&
        newFilters[key as keyof typeof newFilters] !== null
      ) {
        delete (
          newFilters[key as keyof typeof newFilters] as { [key: string]: any }
        )[value];
      } else {
        delete newFilters[key as keyof typeof newFilters];
      }
      handleSideFiltersChange(newFilters);
    },
    [sideFilters, handleSideFiltersChange]
  );

  const {
    handleFilterChange,
    handleCategoryChange,
    handleBikeSelection,
    handleSizeChange,
    handlePriceRangeChange,
    activePage,
    setActivePage,
    pendingFilterChanges,
    setPendingFilterChanges,
  } = useCategoryFilters({
    filterState: sideFilters,
    setFilterState: handleSideFiltersChange,
  });

  return (
    <div className="w-full sm:mx-auto">
      <div className="flex sm:mx-auto sm:max-w-content">
        {current_cat?.is_master_product ? (
          <MasterProductCategoryHeader
            category={current_cat}
            itemsAvailable={initialPageInfo.totalResults}
          />
        ) : (
          <CategoryPageHeader
            categories={categories}
            current_cat={current_cat}
            searchTitle={
              keyword
                ? `${initialPageInfo.totalResults} Results for "${keyword}"`
                : props.h1 ?? ''
            }
            filterState={sideFilters}
          />
        )}
      </div>

      {(!current_cat?.product_cats.category ||
        ['Bike Parts', 'Accessories'].includes(
          current_cat?.product_cats.category
        )) && (
        <>
          <div className="mt-8 flex w-full sm:-mb-16 sm:hidden sm:px-[9.5rem]">
            {userDoc ? (
              <ShopYourGarage
                onBikeComplete={onBikeComplete}
                sideFilters={sideFilters}
                handleFilterRemove={handleFilterRemove}
                showMobileEditGarageModal={showMobileEditGarageModal}
                setShowMobileEditGarageModal={setShowMobileEditGarageModal}
              />
            ) : (
              <ShopByVehicle
                onBikeComplete={onBikeComplete}
                sideFilters={sideFilters}
                setShowMobileEditGarageModal={setShowMobileEditGarageModal}
              />
            )}
          </div>
          <div className="my-16 hidden sm:my-0 sm:block">
            <ShopByVehicle
              onBikeComplete={onBikeComplete}
              sideFilters={sideFilters}
              setShowMobileEditGarageModal={setShowMobileEditGarageModal}
            />
          </div>
        </>
      )}

      <PageMainContent
        view={topFilters.view}
        isFetching={isFetching}
        setIsFetching={setIsFetching}
        products={results}
        pageInfo={initialPageInfo}
        filterState={sideFilters}
        onFiltersChange={handleSideFiltersChange}
        attribution={{
          data_source: 'typesense',
          page: pathname,
          section: `category-page-${topFilters.view}`,
        }}
        clearAllFilters={clearAllFilters}
        filters={
          <div className="w-[38rem]">
            <Filters
              categories={categories}
              filterState={sideFilters}
              handleFilterChange={handleFilterChange}
              handleCategoryChange={handleCategoryChange}
              handleBikeSelection={handleBikeSelection}
              handleSizeChange={handleSizeChange}
              handlePriceRangeChange={handlePriceRangeChange}
              pendingFilterChanges={pendingFilterChanges}
              setPendingFilterChanges={setPendingFilterChanges}
              mostly_parts={mostly_parts}
              facet_counts={facet_counts}
              pageType={'category'}
              activePage={activePage}
              setActivePage={setActivePage}
            />
          </div>
        }
        controls={
          <SearchControls
            keyword={keyword ?? ''}
            showSearch={false}
            handleConditionsChange={(value) =>
              handleFilterChange('conditions', value)
            }
            handleSellerTypeChange={(value) =>
              handleFilterChange('sellerType', value)
            }
            handleSaleChange={(value) => handleFilterChange('onsale', value)}
            handleSortChange={handleSortChange}
            setDrawerOpen={setDrawerOpen}
            topFilters={topFilters}
            pageType={'category'}
            dropdownOpen={dropdownOpen}
            buttonToggles={buttonToggles}
            handleSearchChange={handleSearchChange}
            setDropdownOpen={setDropdownOpen}
            filterState={sideFilters}
            setFilterState={handleSideFiltersChange}
            currentCat={current_cat}
            facet_counts={facet_counts}
            mostly_parts={mostly_parts}
            categories={categories}
          />
        }
      />

      <div className="w-full  border-zinc-200 bg-[#F4F4F4] py-8">
        <div className="w-full sm:mx-auto sm:max-w-content">
          {!!current_cat && (
            <div className=" w-full gap-[1.2rem] px-8 sm:block sm:px-0">
              <div className="flex gap-x-[1.5rem] py-12 sm:mx-auto sm:max-w-content">
                <div>
                  <h2 className="mb-2 text-left text-[2rem] font-semibold">
                    {sideFilters.bikeMakes?.length
                      ? sideFilters.bikeMakes[0]
                      : ''}{' '}
                    {sideFilters.bikeModels?.length
                      ? sideFilters.bikeModels[0]
                      : ''}{' '}
                    {props.h1 ?? current_cat.name}
                  </h2>

                  {(current_cat.second_description ||
                    props.bottomText ||
                    current_cat.meta_desc) && (
                    <div
                      className="whitespace-pre-line text-zinc-600"
                      dangerouslySetInnerHTML={{
                        __html: (current_cat.second_description ||
                          props.bottomText ||
                          current_cat.meta_desc)!
                          .replaceAll(
                            /{size}/g,
                            `${
                              sideFilters?.numberSizes[0] ||
                              sideFilters?.letterSizes[0]
                                ? `size ${
                                    sideFilters.numberSizes[0] ||
                                    sideFilters.letterSizes[0]
                                  }`
                                : ''
                            }`
                          )
                          .replaceAll(
                            /{brand}/g,
                            `${
                              sideFilters?.brands[0]
                                ? sideFilters.brands[0]
                                : ''
                            }`
                          ),
                      }}
                    ></div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {current_cat?.product_cats.category === 'Bike Parts' &&
        current_cat.product_cats.ride_type === 'Dirt Bikes' &&
        !sideFilters.bikeMakes?.length && (
          <div className="w-full bg-[#F4F4F4] pb-8">
            <div className="flex flex-col items-start justify-between gap-4 px-8 py-4 sm:mx-auto sm:max-w-content sm:px-0">
              <span className="text-[1.8rem] font-semibold">Shop by Make</span>
              <div className="grid w-full grid-cols-2 gap-y-[1.6rem]  sm:grid-cols-3 ">
                {[
                  'Beta',
                  'Cobra',
                  'Gas Gas',
                  'Honda',
                  'Husqvarna',
                  'KTM',
                  'Kawasaki',
                  'Suzuki',
                  'Yamaha',
                ].map((make) => (
                  <Link
                    className="underline"
                    key={make}
                    href={current_cat.full_slug
                      .split('/')
                      .slice(0, -1)
                      .join('/')
                      .concat(`/${slugify(make)}-`)
                      .concat(
                        current_cat.full_slug.split('/').slice(-1).join('/')
                      )}
                  >
                    {make} {current_cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      {!!categories.length && (
        <div className="w-full bg-[#F4F4F4] pb-16">
          <div className="flex flex-col items-start justify-between gap-4 px-8 py-4 sm:mx-auto sm:max-w-content sm:px-0">
            <span className="text-[1.8rem] font-semibold">
              Shop by Category
            </span>
            <div className="grid w-full grid-cols-2 gap-y-[1.6rem]  sm:grid-cols-3 ">
              {categories.map((category) => (
                <Link
                  className="underline"
                  key={category.full_slug}
                  href={category.full_slug}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
